import { businessQuotesService } from '@/services/quotes/business';
import type { RejectQuoteInAnalysisResponse } from '@/services/quotes/types';
import { clearClonedQuoteDraftMark } from '@/shared/utils/clone-quote-status.util';
import { useMutation, useQueryClient } from '@tanstack/react-query';

type Variables = {
  policyId: string;
  description: string;
};

/**
 * Recusa uma cotação em análise. POST /portal/v1/business/quotes/:policyId/reject-analysis
 * com `{ description }` no body. Acionado pelo botão "Recusar" na tela de análise.
 *
 * O backend devolve `refusalLetterGenerated` e (quando gerada) `refusalLetterKey`;
 * o caller deve usar esses campos para abrir/baixar a carta de recusa.
 */
export function useRejectAnalysis() {
  const queryClient = useQueryClient();

  return useMutation<RejectQuoteInAnalysisResponse, Error, Variables>({
    mutationKey: ['quotes', 'reject-analysis'],
    mutationFn: async ({ policyId, description }) => {
      const response = await businessQuotesService.rejectAnalysis(
        policyId,
        description
      );

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Erro ao recusar cotação em análise');
      }

      return response.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['proposal-details', variables.policyId],
      });
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      queryClient.invalidateQueries({ queryKey: ['quotes-status-count'] });
      // Após reject-analysis o BFF persiste a carta de recusa em S3 — invalida
      // qualquer cache de documentos pra refletir a nova entrada (refusal_letter).
      queryClient.invalidateQueries({
        queryKey: ['quote-document', variables.policyId],
      });
      clearClonedQuoteDraftMark(variables.policyId);
    },
  });
}
